import clsx from "clsx";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";

import { Checkbox } from "@/shared/components/Checkbox";
import { CheckboxInput } from "@/shared/components/CheckboxInput";
import { DocumentUploadGrid } from "@/shared/components/DocumentUploadGrid";
import { RadioInput } from "@/shared/components/RadioInput";
import { SelectInput } from "@/shared/components/SelectInput";
import { TextInput } from "@/shared/components/TextInput";
import { TextareaInput } from "@/shared/components/TextareaInput";
import type { StepFormComponentProps, StepFormHandle } from "@/shared/stepper/types";
import {
  DocumentUploadField,
  FieldKey,
  PersistedUploadedDocument,
} from "../types/documentuploadtype";
import { BooleanRecord, FormRecord, PostQuoteDocumentUploadConfig } from "../types/preQuote";

export function createPostQuoteDocumentUploadStep<TValues extends BooleanRecord>({
  title = "Required documents",
  description = "Click a card to mark that document as uploaded.",
  defaultValues,
  confirmKey,
  fields,
  confirmLabel = "I confirm that I have uploaded the required documents.",
  errorMessage = "Please confirm the document upload before moving to the next step.",
  guidelines = [
    "Accepted formats: PDF, JPG, PNG",
    "Make sure your details are clearly visible",
    "Blurry or cropped files may delay approval",
  ],
  uploadEndpoint,
  uploadFileFieldName,
  uploadFields,
  ocrEndpointPath,
  ocrFileFieldName,
  ocrFields,
  label,
  isShowUploadGuidelines = true,
}: PostQuoteDocumentUploadConfig<TValues>) {
  type DocumentStepExtraProps = {
    isReadOnly?: boolean;
    hiddenFieldKeys?: Array<FieldKey<TValues>>;
    persistedDocuments?: Partial<
      Record<FieldKey<TValues>, PersistedUploadedDocument<FieldKey<TValues>>>
    >;
    isShowUploadGuidelines?: boolean;
    onUploadComplete?: (params: {
      field: DocumentUploadField<TValues>;
      affectedFields?: Array<DocumentUploadField<TValues>>;
      file: File;
      uploadResponse?: unknown;
      ocrResponse?: unknown;
      persistedDocument: PersistedUploadedDocument<FieldKey<TValues>>;
    }) => void;
    onConfirmChange?: (checked: boolean) => boolean | Promise<boolean> | void;
    enableAutoConfirmWhenRequiredUploaded?: boolean;
    showTitle?: boolean;
    onRemoveDocument?: (key: string, affectedKeys?: Array<FieldKey<TValues>>) => void;
  };

  return forwardRef<
    StepFormHandle<TValues>,
    StepFormComponentProps<TValues, DocumentStepExtraProps>
  >(function PostQuoteDocumentUploadStep(props, ref) {
    const incomingValues = props.defaultValues as TValues | undefined;
    const enableAutoConfirmWhenRequiredUploaded =
      props.enableAutoConfirmWhenRequiredUploaded === true;
    const hiddenFieldKeys = props.hiddenFieldKeys || [];
    const isGuidelinesVisible = props.isShowUploadGuidelines ?? isShowUploadGuidelines;
    const isTitleVisible = props.showTitle ?? true;
    const visibleFields = useMemo(
      () => fields.filter((field) => !hiddenFieldKeys.includes(field.key)),
      [hiddenFieldKeys, fields]
    );
    const persistedDocuments = props.persistedDocuments;
    const [form, setForm] = useState<TValues>(incomingValues ?? defaultValues);
    const [showError, setShowError] = useState(false);
    const [hasSubmitAttempted, setHasSubmitAttempted] = useState(false);
    const [uploadErrors, setUploadErrors] = useState<
      Partial<Record<Exclude<keyof TValues, symbol>, string>>
    >({});

    useEffect(() => {
      setForm(incomingValues ?? defaultValues);
      setShowError(false);
      setHasSubmitAttempted(false);
      setUploadErrors({});
    }, [defaultValues, incomingValues]);

    useEffect(() => {
      if (!persistedDocuments) {
        return;
      }

      const hasMissingUploadedFlags = visibleFields.some(
        (field) =>
          Boolean(persistedDocuments[field.key]) &&
          (incomingValues ?? defaultValues)[field.key] !== true
      );

      if (!hasMissingUploadedFlags) {
        return;
      }

      setForm((current) => {
        const nextValues = { ...current } as TValues;

        visibleFields.forEach((field) => {
          if (persistedDocuments[field.key]) {
            nextValues[field.key] = true as TValues[typeof field.key];
          }
        });

        return nextValues;
      });
    }, [defaultValues, incomingValues, persistedDocuments, visibleFields]);

    const getUploadValidationErrors = (nextValues: TValues) =>
      visibleFields.reduce<Partial<Record<Exclude<keyof TValues, symbol>, string>>>(
        (accumulator, field) => {
          if (field.required && nextValues[field.key] !== true) {
            accumulator[field.key] = `${field.label || field.title} is required.`;
          }

          return accumulator;
        },
        {}
      );

    const hasUploadErrors = Object.keys(uploadErrors).length > 0;
    const shouldShowValidationSummary = hasSubmitAttempted && (hasUploadErrors || showError);

    const shouldAutoConfirmDocuments = (nextValues: TValues) =>
      visibleFields
        .filter((field) => field.required)
        .every((field) => nextValues[field.key] === true);

    const syncValidationState = (nextValues: TValues) => {
      if (!hasSubmitAttempted) {
        return;
      }

      setUploadErrors(getUploadValidationErrors(nextValues));
      setShowError(nextValues[confirmKey] !== true);
    };

    const handleDocumentChange = (nextValues: TValues) => {
      const isAutoConfirmEligible =
        enableAutoConfirmWhenRequiredUploaded &&
        nextValues[confirmKey] !== true &&
        shouldAutoConfirmDocuments(nextValues);

      if (isAutoConfirmEligible) {
        const autoConfirmedValues = {
          ...nextValues,
          [confirmKey]: true,
        } as TValues;

        setForm(autoConfirmedValues);
        syncValidationState(autoConfirmedValues);
        return;
      }

      setForm(nextValues);
      syncValidationState(nextValues);
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const nextUploadErrors = getUploadValidationErrors(form);
        const hasUploadErrors = Object.keys(nextUploadErrors).length > 0;
        const isConfirmValid = form[confirmKey] === true;

        setHasSubmitAttempted(true);
        setUploadErrors(nextUploadErrors);
        setShowError(!isConfirmValid);

        return !hasUploadErrors && isConfirmValid;
      },
      getValues: () => form,
    }));

    return (
      <div className="space-y-6">
        {isTitleVisible ? (
          <div className="mb-4">
            <h2 className="text-lg text-dark">{title}</h2>
            {description ? (
              <p className="mt-2 text-sm text-theme-text-soft">{description}</p>
            ) : null}
          </div>
        ) : null}
        <fieldset disabled={props.isReadOnly === true} className="contents">
          <DocumentUploadGrid
            fields={visibleFields}
            values={form}
            disabled={props.isReadOnly === true}
            label={label}
            onChange={handleDocumentChange}
            fieldErrors={uploadErrors}
            uploadEndpoint={uploadEndpoint}
            uploadFileFieldName={uploadFileFieldName}
            uploadFields={uploadFields}
            ocrEndpointPath={ocrEndpointPath}
            ocrFileFieldName={ocrFileFieldName}
            ocrFields={ocrFields}
            initialDocuments={persistedDocuments}
            onUploadComplete={props.onUploadComplete}
            onRemoveDocument={props.onRemoveDocument}
          />
        </fieldset>
        {isGuidelinesVisible && (
          <>
            {guidelines.length > 0 ? (
              <div className="rounded-2xl border border-theme-border bg-theme-surface-subtle p-4 mt-4">
                <h3 className="text-sm font-semibold text-theme-text">📌 Document guidelines</h3>
                <ul className="mt-3 list-disc space-y-2 pl-5 text-[11px] text-theme-text-soft">
                  {guidelines.map((guideline) => (
                    <li className="mb-0.5" key={guideline}>
                      {guideline}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}

        <fieldset disabled={props.isReadOnly === true} className="contents">
          <label
            className={clsx(
              "flex items-start gap-3 rounded-xl border px-3 py-2 align-middle cursor-pointer transition-colors",
              showError ? "border-red-500 bg-red-50/40" : "border-theme-border"
            )}
          >
            <Checkbox
              color="green"
              className="mt-1"
              disabled={props.isReadOnly === true}
              checked={form[confirmKey] === true}
              onChange={async (event) => {
                if (props.isReadOnly === true) {
                  return;
                }

                const checked = event.target.checked;

                if (checked) {
                  const nextUploadErrors = getUploadValidationErrors(form);
                  const hasValidationErrors = Object.keys(nextUploadErrors).length > 0;

                  setHasSubmitAttempted(true);
                  setUploadErrors(nextUploadErrors);

                  if (hasValidationErrors) {
                    setShowError(true);
                    setForm(
                      (current) =>
                        ({
                          ...current,
                          [confirmKey]: false,
                        }) as TValues
                    );
                    return;
                  }
                }

                const nextValues = {
                  ...form,
                  [confirmKey]: checked,
                } as TValues;

                setForm(nextValues);
                syncValidationState(nextValues);

                queueMicrotask(async () => {
                  const accepted = await props.onConfirmChange?.(checked);
                  if (checked && accepted === false) {
                    setForm((current) => {
                      const revertedValues = {
                        ...current,
                        [confirmKey]: false,
                      } as TValues;
                      syncValidationState(revertedValues);
                      return revertedValues;
                    });
                  }
                });
              }}
            />
            <div className="cursor-pointer">
              <div className="font-medium text-[12px] text-theme-text cursor-pointer">
                {confirmLabel}
              </div>
              {showError ? (
                <p className="mt-1 text-[11px] font-medium text-red-600">{errorMessage}</p>
              ) : null}
            </div>
          </label>
        </fieldset>
      </div>
    );
  });
}

export type PostQuoteInfoField<TValues extends FormRecord> = {
  key: Exclude<keyof TValues, symbol>;
  label: string;
  type: "input" | "textarea" | "select" | "checkbox";
  placeholder?: string;
  options?: string[];
};

type PostQuoteAdditionalInfoConfig<TValues extends FormRecord> = {
  title?: string;
  description?: string;
  defaultValues: TValues;
  fields: Array<PostQuoteInfoField<TValues>>;
};

export function createPostQuoteAdditionalInfoStep<TValues extends FormRecord>({
  title = "Additional information",
  description = "Add any final details the insurer may need before policy issuance.",
  defaultValues,
  fields,
}: PostQuoteAdditionalInfoConfig<TValues>) {
  return forwardRef<StepFormHandle<TValues>, StepFormComponentProps<TValues>>(
    function PostQuoteAdditionalInfoStep(props, ref) {
      const incomingValues = props.defaultValues as TValues | undefined;
      const form = useForm<any>({
        defaultValues: incomingValues ?? defaultValues,
      });

      useEffect(() => {
        form.reset(incomingValues ?? defaultValues);
      }, [defaultValues, form, incomingValues]);

      useImperativeHandle(ref, () => ({
        submit: async () => true,
        getValues: () => form.getValues() as TValues,
      }));

      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-theme-text">{title}</h2>
            <p className="mt-2 text-sm text-theme-text-soft">{description}</p>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {fields
              .filter((field) => field.type !== "textarea" && field.type !== "checkbox")
              .map((field) => (
                <div key={String(field.key)}>
                  {field.type === "select" ? (
                    <SelectInput
                      control={form.control}
                      name={String(field.key)}
                      label={field.label}
                      placeholder={field.placeholder || `Select ${field.label.toLowerCase()}`}
                      options={(field.options || []).map((option) => ({
                        label: option,
                        value: option,
                      }))}
                      className="gap-2"
                    />
                  ) : (
                    <TextInput
                      control={form.control}
                      name={String(field.key)}
                      label={field.label}
                      placeholder={field.placeholder}
                      className="gap-2"
                    />
                  )}
                </div>
              ))}
          </div>

          {fields
            .filter((field) => field.type === "textarea")
            .map((field) => (
              <TextareaInput
                key={String(field.key)}
                control={form.control}
                name={String(field.key)}
                label={field.label}
                placeholder={field.placeholder}
              />
            ))}

          {fields
            .filter((field) => field.type === "checkbox")
            .map((field) => (
              <CheckboxInput
                key={String(field.key)}
                control={form.control}
                name={String(field.key)}
                label={field.label}
                checkboxLabel={field.label}
              />
            ))}
        </div>
      );
    }
  );
}

type PaymentValues = {
  paymentMethod: string;
  billingEmail: string;
  confirmPayment: boolean;
};

type PostQuotePaymentConfig<TValues extends PaymentValues> = {
  title?: string;
  description?: string;
  defaultValues: TValues;
  paymentOptions?: string[];
  optionHelper?: string;
  confirmLabel?: string;
  errorMessage?: string;
};

export function createPostQuotePaymentStep<TValues extends PaymentValues>({
  title = "Payment",
  description = "Select a payment method to complete the purchase.",
  defaultValues,
  paymentOptions = ["Card", "Bank Transfer", "Cash Collection"],
  optionHelper = "Payment option",
  confirmLabel = "I confirm that this payment step is ready to proceed.",
  errorMessage = "Please confirm payment before completing the flow.",
}: PostQuotePaymentConfig<TValues>) {
  return forwardRef<StepFormHandle<TValues>, StepFormComponentProps<TValues>>(
    function PostQuotePaymentStep(props, ref) {
      const incomingValues = props.defaultValues as TValues | undefined;
      const form = useForm<any>({
        defaultValues: incomingValues ?? defaultValues,
      });
      const [showError, setShowError] = useState(false);

      useImperativeHandle(ref, () => ({
        submit: async () => {
          const isValid = form.getValues("confirmPayment") === true;
          setShowError(!isValid);
          return isValid;
        },
        getValues: () => form.getValues() as TValues,
      }));

      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold text-theme-text">{title}</h2>
            <p className="mt-2 text-sm text-theme-text-soft">{description}</p>
          </div>

          <RadioInput
            control={form.control}
            name="paymentMethod"
            label="Payment Method"
            description={optionHelper}
            direction="column"
            options={paymentOptions.map((option) => ({
              label: option,
              value: option,
            }))}
          />

          <TextInput
            control={form.control}
            name="billingEmail"
            label="Billing Mail"
            type="email"
            // placeholder="name@example.com"
          />

          <Controller
            control={form.control}
            name="confirmPayment"
            render={({ field }) => (
              <label className="flex items-start gap-3 rounded-2xl border border-theme-border p-4">
                <Checkbox
                  color="green"
                  checked={field.value === true}
                  onChange={(event) => field.onChange(event.target.checked)}
                  className="mt-1"
                />
                <div>
                  <div className="font-medium text-theme-text">{confirmLabel}</div>
                  {showError ? <p className="mt-1 text-sm text-red-600">{errorMessage}</p> : null}
                </div>
              </label>
            )}
          />
        </div>
      );
    }
  );
}
